import i18n from "../i18n";
/**
 * Bookmark Service - Simplified API-focused implementation
 *
 * This service provides clean helper functions for bookmark operations.
 * The API is the single source of truth - no localStorage for bookmarks.
 *
 * Usage pattern:
 * 1. Fetch bookmarks on document load via fetchBookmarks()
 * 2. Check bookmark status via isPageBookmarked(bookmarksData, pageNumber)
 * 3. Toggle bookmarks via toggleBookmark({ pageNumber, ... })
 * 4. Delete bookmarks via deleteBookmarkById()
 */

import {
  createBookmark as createBookmarkApi,
  deleteBookmark as deleteBookmarkApi,
  fetchAccessData,
  getSessionTokens,
  getValidRstToken,
} from "./igpService";
import {
  showOperationFailedNotification,
  showSessionExpiredNotification,
} from "./notificationService";

/**
 * Generate a unique book ID for local storage purposes (used by notes, etc.)
 * @param {string} documentName - The document file name
 * @param {string} documentType - 'pdf' or 'epub'
 * @param {Object} document - The document object
 * @returns {string} - Unique book ID
 */
export function getBookId(documentName, documentType, document) {
  try {
    const extra =
      documentType === "pdf"
        ? document?.numPages || ""
        : documentType === "epub"
          ? document?.spine?.length || ""
          : "";
    return `${documentType || "unknown"}:${
      documentName || "untitled"
    }:${extra}`;
  } catch (e) {
    return `${documentType || "unknown"}:${documentName || "untitled"}`;
  }
}

/**
 * Check if a specific page is bookmarked
 * @param {Array} bookmarksData - Array of bookmark objects from store
 * @param {number} pageNumber - Page number to check
 * @returns {boolean}
 */
export function isPageBookmarked(bookmarksData, pageNumber) {
  if (!Array.isArray(bookmarksData) || !pageNumber) return false;
  return bookmarksData.some((bm) => bm.page_number === pageNumber);
}

/**
 * Find a bookmark by page number
 * @param {Array} bookmarksData - Array of bookmark objects from store
 * @param {number} pageNumber - Page number to find
 * @returns {Object|undefined} - The bookmark object or undefined
 */
export function findBookmarkByPage(bookmarksData, pageNumber) {
  if (!Array.isArray(bookmarksData) || !pageNumber) return undefined;
  return bookmarksData.find((bm) => bm.page_number === pageNumber);
}

const createBookmarkId = () =>
  `bmk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Fetch bookmarks from API
 * @returns {Promise<Array>} - Array of bookmark objects
 */
export async function fetchBookmarks() {
  const { grantId, agtToken } = getSessionTokens();
  const rstToken = await getValidRstToken(grantId, agtToken);
  const accessData = await fetchAccessData(grantId, rstToken, ["bookmarks"]);
  return accessData?.bookmarks?.data || [];
}

/**
 * Toggle bookmark for a page - creates if not exists, deletes if exists.
 * Uses optimistic updates for immediate UI feedback and rolls back on error.
 *
 * @param {Object} params
 * @param {number} params.pageNumber - The page number to bookmark
 * @param {string} params.title - Optional title for the bookmark
 * @param {Array} params.currentBookmarks - Current bookmarks array from store
 * @param {Function} params.onSuccess - Callback with updated bookmarks array
 * @param {Function} params.onError - Callback with error object
 * @returns {Promise<void>}
 */
/**
 * Internal: Persist bookmark toggle to API in background.
 */
async function _persistBookmarkToggle(
  isCreate,
  bookmarkData,
  previousBookmarks,
  onSuccess,
  onError,
) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);

    if (isCreate) {
      await createBookmarkApi(grantId, rstToken, bookmarkData);
    } else {
      await deleteBookmarkApi(grantId, rstToken, bookmarkData.id);
    }
  } catch (error) {
    console.error("Error persisting bookmark toggle:", error);
    // Roll back optimistic update on failure.
    onSuccess?.(previousBookmarks);
    if (error.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.saveBookmark"));
    }
    onError?.(error);
  }
}

/**
 * Toggle bookmark for a page - creates if not exists, deletes if exists.
 * Uses fully OPTIMISTIC UPDATES for immediate UI feedback.
 */
export function toggleBookmark({
  pageNumber,
  title = "",
  currentBookmarks = [],
  onSuccess,
  onError,
}) {
  const previousBookmarks = Array.isArray(currentBookmarks) ? currentBookmarks : [];

  const existing = findBookmarkByPage(previousBookmarks, pageNumber);
  const bookmarkToCreate = existing
    ? null
    : {
        id: createBookmarkId(),
        page_number: pageNumber,
        title: title || `Page ${pageNumber}`,
        timestamp: new Date().toISOString(),
      };

  const nextBookmarks = existing
    ? previousBookmarks.filter((bm) => bm.id !== existing.id)
    : [...previousBookmarks, bookmarkToCreate];

  // Update UI immediately (Optimistic)
  onSuccess?.(nextBookmarks);

  // Background sync
  _persistBookmarkToggle(
    !existing,
    existing || bookmarkToCreate,
    previousBookmarks,
    onSuccess,
    onError,
  );
}

/**
 * Delete a bookmark by its ID with OPTIMISTIC UPDATE.
 */
export function deleteBookmarkById({
  bookmarkId,
  currentBookmarks,
  onSuccess,
  onError,
}) {
  const previousBookmarks = Array.isArray(currentBookmarks) ? currentBookmarks : [];
  const optimistic = previousBookmarks.filter((bm) => bm.id !== bookmarkId);

  // Update UI immediately
  onSuccess?.(optimistic);

  // Background sync
  _persistBookmarkDelete(bookmarkId, previousBookmarks, onSuccess, onError);
}

/**
 * Internal: Persist bookmark deletion to API in background.
 */
async function _persistBookmarkDelete(
  bookmarkId,
  previousBookmarks,
  onSuccess,
  onError,
) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    await deleteBookmarkApi(grantId, rstToken, bookmarkId);
  } catch (error) {
    console.error("Error persisting bookmark delete:", error);
    // Roll back on failure
    onSuccess?.(previousBookmarks);
    if (error.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.deleteBookmark"));
    }
    onError?.(error);
  }
}


/**
 * Create a bookmark label based on document type
 * Utility function for generating default bookmark titles
 *
 * @param {string} documentType - 'pdf' or 'epub'
 * @param {number} pageNumber - The page/section number
 * @returns {string} - Generated label
 */
export function createBookmarkLabel(documentType, pageNumber) {
  if (documentType === "pdf") {
    return i18n.t("notes.pageTitle", { page: pageNumber });
  }
  if (documentType === "epub") {
    return `${i18n.t("common.section")} ${pageNumber}`;
  }
  return i18n.t("bookmarks.defaultLabel", { page: pageNumber });
}
